import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { hasFilterMemory } from '@/lib/filter-memory/use-filter-memory';

/**
 * Saved list filters, applied on arrival and written INTO the URL.
 *
 * The owner's four rulings (2026-08-30):
 *
 *   1. Preferences are stored server-side against the account.
 *   2. Saved filters WRITE INTO THE URL — arriving at a page whose address
 *      names no filters applies the saved ones and puts them in the query
 *      string, so what is on screen is always what the address says.
 *   3. A LINK WINS ENTIRELY. If the incoming URL names ANY of the page's
 *      filter keys, saved filters do not apply at all. Not a per-key merge:
 *      the recipient sees exactly what the sender saw.
 *   4. View, page size and sort may ride along — see `independentKeys`, which
 *      deliberately follows a DIFFERENT rule.
 *
 * ## Transport
 *
 * This module knows nothing about how a preference is stored. It takes a
 * `SavedFilterStore` and never imports the API client, so the server-backed
 * preferences store can be dropped in without this file changing, and every
 * decision below is testable against a store that is a plain object.
 *
 * ## Who writes the URL
 *
 * This hook never writes the query string itself. It hands the page a patch
 * through `apply`, and the page applies it through its own single writer
 * (`update` on every list page here). That is deliberate: each page's writer is
 * the one place that knows how to canonicalise its own params — it drops values
 * equal to the server's default so there is ONE URL per view, and it resets
 * `page` to 1, because page 4 of the old result set is meaningless in the new
 * one. A hook calling `setSearchParams` directly would bypass both.
 *
 * Two preconditions the caller owns, and both have bitten this repo:
 *
 *   - the page's writer must pass `{ replace: true }` to `setSearchParams`, so
 *     seeding a saved filter is not a history entry the Back button has to walk
 *     through;
 *   - `useSearchParams()` takes NO argument. Its first parameter is a default
 *     init, not options, so `useSearchParams({ replace: true })` writes a
 *     literal `replace=true` into the query string and then into the parsed
 *     filters.
 *
 * ## The three arrival states
 *
 * They look identical in the URL a moment after the fact, and conflating them
 * is the whole difficulty. Same mechanism as the current-cycle default on
 * `/projects` (`defaultCycleSettled` in `ProjectsPage.tsx`):
 *
 *   1. first arrival, no filter key named  -> apply saved, write them in
 *   2. an explicit link naming filters     -> settled untouched; THE LINK WINS
 *   3. the user CLEARED the filters        -> already settled; stays cleared
 *
 * Getting (3) wrong is the classic failure: the user clears a filter, the param
 * disappears, the "no filters named" condition is true again, the default
 * re-applies, and the control reads as broken.
 *
 * A ref settles it, not state: nothing renders from "have we decided yet", and
 * the decision must survive a re-render that happens mid-flight.
 *
 * "Once" means once per MOUNT, deliberately. Leaving the section and coming
 * back re-seeds. Remembering a cleared filter across a mount would put a second
 * source of truth beside the URL and make one URL render differently for two
 * people — the exact thing ruling 3 exists to prevent.
 *
 * ## Clearing is the undo, which is why there is no `forget()`
 *
 * Never persist a value the page cannot later clear. Here that is structural
 * rather than a rule someone has to remember: the cleared state is itself
 * persisted. A user who clears every filter saves an empty set, and the next
 * arrival applies nothing. So the affordance that undoes a bad saved filter is
 * the filter's own clear control, which is already on screen — no second
 * "forget my filters" control to find, and no state reachable only through one.
 *
 * The caller's half of that bargain: put a key in `keys` only if the page
 * renders a control that can clear it. A persisted key with no control is a
 * filter the reader cannot see and cannot undo, which is the same defect as an
 * un-clearable default wearing different clothes.
 */

/** What is stored. Flat string map — one page's filter keys and their values. */
export type SavedFilterValues = Record<string, string>;

/**
 * The injected transport.
 *
 * `load` rejecting, resolving `null`, or never resolving at all must all be
 * indistinguishable from having no saved filters: the page renders unfiltered.
 * A preferences read is never allowed to hold a list up (see `loadBudgetMs`).
 *
 * `save` is fire-and-forget from this hook's point of view. It is given the
 * complete set for the scope — a whole-set REPLACE, not a patch — because a
 * cleared filter has to be expressible, and `{}` is how it is expressed.
 */
export interface SavedFilterStore {
  load(scope: string, signal: AbortSignal): Promise<SavedFilterValues | null>;
  save(scope: string, values: SavedFilterValues): Promise<void>;
}

/* ---------------------------------------------------------------------------
 * The pure half
 *
 * Everything that decides anything lives here: no React, no store, no clock.
 * The hook below is plumbing around these four functions, which is what makes
 * the behaviour testable without a DOM.
 * ------------------------------------------------------------------------ */

/**
 * Does the incoming URL name any of this page's filter keys?
 *
 * Presence, not truthiness. `?cycleId=` names the key: somebody wrote it, and a
 * link that says "no cycle" is still a link saying something. Every
 * `writeState` in this app deletes an empty value rather than emitting one, so
 * a bare key can only come from a hand-edited or hand-built address — which is
 * exactly the case where the sender's intent should win.
 */
export function namesAnyKey(params: URLSearchParams, keys: readonly string[]): boolean {
  return keys.some((key) => params.has(key));
}

/**
 * The values the URL currently holds for `keys`.
 *
 * Empty and whitespace-only values are dropped rather than recorded as `''`: a
 * key present with no value is not a filter, and storing it would make the
 * saved set differ from the cleared set that produced it.
 */
export function readFilterValues(
  params: URLSearchParams,
  keys: readonly string[],
): SavedFilterValues {
  const values: SavedFilterValues = {};
  for (const key of keys) {
    const raw = params.get(key);
    if (raw === null || raw.trim() === '') continue;
    values[key] = raw;
  }
  return values;
}

/**
 * A stable string for a set of values, for "has anything changed since?".
 *
 * Key order is normalised, so two URLs differing only in the order their params
 * were written compare equal and do not trigger a pointless network write.
 */
export function filterSignature(values: SavedFilterValues, keys: readonly string[]): string {
  return [...keys]
    .sort()
    .map((key) => {
      const value = values[key];
      return value === undefined ? '' : `${key}=${encodeURIComponent(value)}`;
    })
    .filter((part) => part !== '')
    .join('&');
}

/**
 * What comes back from the store, made safe.
 *
 * The store is server-backed and its payload is not this app's to guarantee, so
 * anything unrecognised is dropped rather than trusted: unknown keys (a key the
 * page no longer renders is a filter nobody can see or clear), non-string
 * values, and empty strings. Junk degrades to "no saved filters", never to a
 * throw — a preferences response that changed shape must not take a list down.
 *
 * No length cap here, on purpose. The page's own `readState` already truncates
 * an over-long `search`, and a second, different cap in this file would be a
 * silent disagreement with it.
 */
export function sanitiseSavedValues(raw: unknown, keys: readonly string[]): SavedFilterValues {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const source = raw as Record<string, unknown>;
  const values: SavedFilterValues = {};
  for (const key of keys) {
    const value = source[key];
    if (typeof value !== 'string' || value.trim() === '') continue;
    values[key] = value;
  }
  return values;
}

/** Which of the three arrival states this mount is in. */
export type ArrivalState =
  /** The URL named a filter key. Saved filters were ignored ENTIRELY. */
  | 'link-wins'
  /** No filter key named, and saved filters existed — they were applied. */
  | 'seeded'
  /** No filter key named, and there was nothing to apply. */
  | 'nothing-saved'
  /**
   * Filter memory (owner 2026-10-16, `lib/filter-memory`) answered for this
   * page: the remembered address was restored before the page mounted, or the
   * reader's remembered CLEAR stands. Saved filters do not apply (ruling 4),
   * and every other arrival writer must stand down exactly as for `'seeded'`.
   */
  | 'remembered';

export interface ArrivalDecision {
  state: ArrivalState;
  /** The patch for the page's own writer, or `null` when nothing applies. */
  patch: SavedFilterValues | null;
}

/**
 * THE decision. Given the arriving URL and what was stored, what applies?
 *
 * `keys` and `independentKeys` follow OPPOSITE rules, and that asymmetry is the
 * point rather than an inconsistency.
 *
 * A page's filters are a SET whose meaning is joint: "open deals in India over
 * ten thousand" is one statement, and merging half a sender's filters with half
 * a recipient's saved ones produces a screen neither of them has ever seen,
 * under a heading that describes something else. So `keys` is all-or-nothing —
 * any one of them present in the URL and none of the saved ones apply.
 *
 * `independentKeys` — the table-or-board view, rows per page, sort — are
 * single-valued preferences with no joint meaning: a link naming a stage says
 * nothing whatever about how many rows the recipient likes to see. They are
 * therefore merged PER KEY: the saved value applies wherever the URL is silent,
 * and the URL wins wherever it speaks. That holds even under `link-wins`, which
 * is why a sender's `?stageId=3` does not also impose the sender's page size.
 *
 * The patch carries only what is actually being set. In the seeded state every
 * filter key is absent from the URL by definition, so there is nothing to clear
 * and a minimal patch is a complete one.
 */
export function arrivalDecision(
  params: URLSearchParams,
  saved: SavedFilterValues | null,
  keys: readonly string[],
  independentKeys: readonly string[] = [],
): ArrivalDecision {
  const values = saved ?? {};

  // Applied under every state, including link-wins: these have no joint meaning
  // with the filters, and the URL still wins key by key.
  const independentPatch: SavedFilterValues = {};
  for (const key of independentKeys) {
    if (params.has(key)) continue;
    const value = values[key];
    if (value === undefined) continue;
    independentPatch[key] = value;
  }

  if (namesAnyKey(params, keys)) {
    return {
      state: 'link-wins',
      patch: Object.keys(independentPatch).length > 0 ? independentPatch : null,
    };
  }

  const filterPatch: SavedFilterValues = {};
  for (const key of keys) {
    const value = values[key];
    if (value === undefined) continue;
    filterPatch[key] = value;
  }

  const patch = { ...independentPatch, ...filterPatch };
  if (Object.keys(patch).length === 0) return { state: 'nothing-saved', patch: null };
  return {
    // A patch of independent keys alone is not a seeded FILTER state: the list
    // is still unfiltered, and an empty-state that said otherwise would send
    // somebody hunting for a filter to clear.
    state: Object.keys(filterPatch).length > 0 ? 'seeded' : 'nothing-saved',
    patch,
  };
}

/* ---------------------------------------------------------------------------
 * The hook
 * ------------------------------------------------------------------------ */

export interface UseSavedFiltersOptions<K extends string> {
  /** Storage key for this page's preference, e.g. `'projects.filters'`. */
  scope: string;
  /**
   * The page's FILTER keys, as they are spelled in the query string.
   *
   * This one list does three jobs: it decides whether an incoming link is
   * authoritative, it is what gets applied, and it is what gets persisted.
   * Include a key only if the page renders a control that can clear it, and do
   * NOT include `page` — restoring somebody onto page 7 of a result set they
   * have not seen reads as a broken pager.
   */
  keys: readonly K[];
  /**
   * Preferences with no joint meaning — `view`, `perPage`, `sort`, `order`.
   * Merged per key instead of all-or-nothing; see `arrivalDecision`.
   */
  independentKeys?: readonly K[];
  /** The injected transport. Held by ref, so a fresh object each render is fine. */
  store: SavedFilterStore;
  /**
   * Applies the patch. Pass the page's own single URL writer (`update`), which
   * must use `{ replace: true }`. Called AT MOST ONCE per mount, and never with
   * an empty patch.
   */
  apply: (patch: Partial<Record<K, string>>) => void;
  /**
   * Defaults to `true`. `false` holds the load off (an unauthenticated first
   * paint, say) WITHOUT holding `seeding` open indefinitely — the budget below
   * runs from mount regardless.
   */
  enabled?: boolean;
  /**
   * How long after the last filter change the set is persisted. Defaults to
   * 1200ms — see the note on `useSavedFilters` about why not per change.
   */
  saveDelayMs?: number;
  /**
   * The ceiling on `seeding`, from mount. Defaults to 6000ms.
   *
   * This is what makes "a preferences read that never came back must not block
   * a list" true by construction rather than by the store being well-behaved. A
   * store that hangs costs an unfiltered list, never a blank page.
   *
   * ⚠️ **IT WAS 2000ms, AND THAT NUMBER SILENTLY DISABLED THE WHOLE FEATURE IN
   * PRODUCTION.** Measured on the deployed app, 2026-08-30, `/companies`:
   *
   * | | cold boot | in-app navigation |
   * | --- | --- | --- |
   * | page mounts | 4555ms | on click |
   * | `/preferences` **dispatched** | 5644ms | — |
   * | response | 6403ms | +2618ms |
   * | **elapsed since mount** | **~1848ms** | **~1900ms** |
   *
   * Both land within ~150ms of the old ceiling, so the cold boot lost the race
   * and the warm one won it. The gap is **not** server latency — the response
   * itself takes ~750ms. It is that this read queues behind NINE other API
   * calls at boot (`auth/me`, `xero/connection`, `permissions/me`,
   * `tasks/brief`, `regions`, `user-groups`, `company-stages`, `countries`,
   * `users`) and the browser holds six connections per host.
   *
   * ⚠️ **And losing is SILENT AND PLAUSIBLE**, which is why it survived a green
   * build, a green lint and a passing save: the budget settles the arrival as
   * `nothing-saved`, so the page renders unfiltered — exactly what somebody with
   * no saved filters correctly sees. **The feature appeared to work when
   * clicked through and to do nothing when actually used**, because clicking
   * through is a warm navigation and using it starts with a page load.
   *
   * 6000ms is ~3x the measured figure rather than a round number picked for
   * taste. It is safe to be generous: **nothing here blocks a list on
   * `seeding`** — the only thing that waits on it is `/projects`' default-cycle
   * effect, which is bounded by this and by nothing else.
   */
  loadBudgetMs?: number;
}

export interface SavedFilters {
  /**
   * True until the arrival state is decided. A page MAY hold its first list
   * request while this is true, to avoid firing an unfiltered request that the
   * seeded one immediately supersedes. It is bounded by `loadBudgetMs`, so
   * gating on it cannot hang the page.
   */
  seeding: boolean;
  /** Which of the three arrival states this mount turned out to be. */
  arrival: ArrivalState | null;
  /** The URL named filters, so nothing saved was applied. Ruling 3. */
  linkWon: boolean;
  /**
   * The store's `load` rejected or ran out of budget.
   *
   * Exposed for diagnosis, NOT as something to render: a failed preferences
   * read is indistinguishable from having no saved filters, on purpose, and a
   * page that announces it turns a silent, correct degradation into an error
   * message over a list that is working perfectly well.
   */
  loadFailed: boolean;
  /** The last `save` rejected. Same warning as `loadFailed`. */
  saveFailed: boolean;
}

/**
 * ## When a change is persisted, and why not sooner
 *
 * Debounced ~1200ms after the last change, plus a flush of anything pending
 * when the page unmounts. Every save is a network write in the real deployment,
 * so the two obvious options are both wrong:
 *
 *   - *on every change* — the search box already commits to the URL 250ms after
 *     the last keystroke, so this is a request per 250ms of typing. A dozen
 *     writes to store one search term.
 *   - *on unmount only* — a closed tab, a crash or a hard reload loses the
 *     preference, and the person who set it has no idea it did not stick.
 *
 * Debounce-plus-flush costs one write per burst of adjustment and loses nothing
 * when somebody sets a filter and navigates straight off the page. The flush is
 * deliberately NOT abortable — the request outliving the component is the whole
 * point of it.
 *
 * The arrival state is never saved. The baseline is taken AFTER the arrival
 * decision has been applied, so neither a seeded set (already stored — a
 * pointless write on every page load) nor a link's filters (someone else's
 * choice, which must not silently become the recipient's default) is written
 * back. The first save of a mount is the user's own first adjustment.
 */
export function useSavedFilters<K extends string>(options: UseSavedFiltersOptions<K>): SavedFilters {
  const {
    scope,
    keys,
    independentKeys,
    store,
    apply,
    enabled = true,
    saveDelayMs = 1200,
    // 6000, not 2000 — the old value lost the race on every cold boot and did
    // so silently. See `loadBudgetMs` for the measurement.
    loadBudgetMs = 6000,
  } = options;

  const [searchParams] = useSearchParams();
  // Depend on the string, never the object: its identity changes every render.
  const searchKey = searchParams.toString();
  const memoryPath = useLocation().pathname;

  const [arrival, setArrival] = useState<ArrivalState | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);

  /*
   * `keys` and `independentKeys` are inline literals at every call site, so a
   * fresh array arrives each render. The joined string is what the arrival
   * effect depends on; the arrays themselves are read through the ref below.
   *
   * `allKeys` is deliberately not memoised — nothing depends on its identity.
   */
  const keysSignature = keys.join('|');
  const allKeys = [...keys, ...(independentKeys ?? [])];

  /*
   * Latest-ref for everything the settle path reads. Without it, `apply` (a
   * `useCallback` on the page's own `searchKey`, so a new function on every URL
   * change) would be an effect dependency, and the arrival effect would want to
   * re-run on the very write it had just made.
   */
  const latest = useRef({ scope, keys, independentKeys, store, apply, searchKey, allKeys });

  /*
   * Kept fresh in an effect with no dependency list rather than assigned during
   * render — a ref is not render state, and the repo's own reminder ticker uses
   * the same shape. Declared FIRST so it has already run by the time the
   * effects below read `latest.current`; the initialiser above covers the very
   * first commit either way.
   */
  useEffect(() => {
    latest.current = { scope, keys, independentKeys, store, apply, searchKey, allKeys };
  });

  /** Has the arrival state been decided? A ref: nothing renders from it. */
  const settled = useRef(false);
  /**
   * The signature the URL held once the arrival decision was applied, and
   * thereafter the signature of the last set handed to `save`. Anything that
   * differs from it is a change the user made, and is worth persisting.
   */
  const baseline = useRef<string | null>(null);
  /** A set debounced but not yet written. Flushed on unmount. */
  const pending = useRef<{ values: SavedFilterValues; signature: string } | null>(null);
  const mounted = useRef(true);

  const settle = useCallback((saved: SavedFilterValues | null) => {
    if (settled.current) return;
    settled.current = true;

    const current = latest.current;
    const params = new URLSearchParams(current.searchKey);
    const decision = arrivalDecision(params, saved, current.keys, current.independentKeys ?? []);

    /*
     * The baseline is what the URL will hold once the patch lands, not what it
     * holds now — that is what stops the arrival state being written straight
     * back. A minimal patch is complete here: in the seeded state every filter
     * key is absent from the URL by definition.
     */
    baseline.current = filterSignature(
      { ...readFilterValues(params, current.allKeys), ...(decision.patch ?? {}) },
      current.allKeys,
    );

    if (decision.patch) current.apply(decision.patch as Partial<Record<K, string>>);
    setArrival(decision.state);
  }, []);

  /*
   * The budget, from mount, whatever `enabled` is doing.
   *
   * A store that never answers costs an unfiltered list and nothing else. Doing
   * this inside the load effect instead would leave `seeding` open for ever
   * whenever `enabled` stayed false — the same defect reached by another route.
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      if (settled.current) return;
      setLoadFailed(true);
      settle(null);
    }, loadBudgetMs);
    return () => clearTimeout(timer);
  }, [loadBudgetMs, settle]);

  /* The arrival decision. Runs until it settles, then short-circuits. */
  useEffect(() => {
    if (settled.current) return;
    if (!enabled) return;

    const current = latest.current;
    const params = new URLSearchParams(current.searchKey);

    /*
     * Filter memory answered first (it restores before this page mounted — see
     * `lib/filter-memory/use-filter-memory.ts`), so saved filters stand aside
     * entirely: no load, no patch, and no saves for this mount (see the
     * persisting effect). Never true on a link arrival, which keeps its
     * existing `'link-wins'` behaviour below.
     */
    if (hasFilterMemory(memoryPath, current.searchKey)) {
      settled.current = true;
      baseline.current = filterSignature(readFilterValues(params, current.allKeys), current.allKeys);
      // A one-shot settle, exactly like `settle()` below: it runs once per mount
      // and short-circuits on `settled` after, so it cannot cascade.
      // oxlint-disable-next-line react/set-state-in-effect
      setArrival('remembered');
      return;
    }

    /*
     * State 2, and with no independent keys it needs no request at all: the
     * link is authoritative, so what is stored cannot change the outcome.
     * Reading the store anyway would spend a request to be ignored.
     *
     * With independent keys the request IS needed — a link naming a filter says
     * nothing about the reader's page size — so only the narrow case is
     * short-circuited, and `arrivalDecision` still makes the actual decision.
     */
    if (
      namesAnyKey(params, current.keys) &&
      (current.independentKeys ?? []).length === 0
    ) {
      settle(null);
      return;
    }

    const controller = new AbortController();
    current.store
      .load(current.scope, controller.signal)
      .then((raw) => {
        // Aborted: this mount is gone, or the budget already settled it. A late
        // answer must never move a page that has decided.
        if (controller.signal.aborted || settled.current) return;
        settle(sanitiseSavedValues(raw, latest.current.allKeys));
      })
      .catch(() => {
        if (controller.signal.aborted || settled.current) return;
        // Indistinguishable from having none: the list renders unfiltered.
        setLoadFailed(true);
        settle(null);
      });

    return () => controller.abort();
  }, [enabled, scope, keysSignature, settle, memoryPath]);

  /* Persisting. Debounced; the cleanup is what coalesces a burst of changes. */
  useEffect(() => {
    if (arrival === null) return;
    // Filter memory answered this mount: it stores the reader's edits itself,
    // and the saved set stays the named starting point it was (ruling 4).
    if (arrival === 'remembered') return;

    const values = readFilterValues(new URLSearchParams(searchKey), latest.current.allKeys);
    const signature = filterSignature(values, latest.current.allKeys);
    if (signature === baseline.current) {
      // Back to what was last stored — including the arrival state itself, so a
      // page that merely loads never writes.
      pending.current = null;
      return;
    }

    pending.current = { values, signature };
    const timer = setTimeout(() => {
      const write = pending.current;
      if (!write) return;
      pending.current = null;
      baseline.current = write.signature;
      latest.current.store
        .save(latest.current.scope, write.values)
        .then(() => {
          if (mounted.current) setSaveFailed(false);
        })
        .catch(() => {
          if (mounted.current) setSaveFailed(true);
        });
    }, saveDelayMs);

    return () => clearTimeout(timer);
  }, [arrival, searchKey, saveDelayMs]);

  /*
   * The unmount flush.
   *
   * Declared AFTER the debounce effect so React runs that effect's cleanup
   * first: the timer is cleared, and whatever it was going to write is still in
   * `pending`. Deliberately not aborted and not awaited — outliving the
   * component is the entire point.
   */
  useEffect(() => {
    // Set on the way in as well as cleared on the way out: React 19's
    // development double-mount runs the cleanup once on a component that is
    // very much still alive, and a `mounted` flag that only ever goes false
    // would report every later save as belonging to a dead page.
    mounted.current = true;
    return () => {
      mounted.current = false;
      const write = pending.current;
      pending.current = null;
      if (!write || write.signature === baseline.current) return;
      baseline.current = write.signature;
      void latest.current.store.save(latest.current.scope, write.values).catch(() => {
        // Nothing left to report to: the page is gone. Swallowed rather than
        // left as an unhandled rejection.
      });
    };
  }, []);

  return {
    seeding: arrival === null,
    arrival,
    linkWon: arrival === 'link-wins',
    loadFailed,
    saveFailed,
  };
}

/* ---------------------------------------------------------------------------
 * A store for tests and local work
 * ------------------------------------------------------------------------ */

/* ---------------------------------------------------------------------------
 * The applier every page shares
 * ------------------------------------------------------------------------ */

/**
 * The `apply` callback, built once instead of seven times.
 *
 * Every list page here already owns a bespoke `update`/`writeState` pair, and
 * the tempting thing is to hand each page's own writer in. It is the wrong
 * choice for this one job:
 *
 * - Those writers **normalise, re-derive and reset**. `ProjectsPage`'s re-reads
 *   `perPage` off the cycle, several reset `page` to 1 through a branch that
 *   also fires on an ordinary filter click, and each one re-serialises the
 *   WHOLE state. Applying a seeded patch through that path means the arrival
 *   write is no longer "put these keys in the address" — it is "run this
 *   page's entire normalisation over a state nobody has touched yet", and what
 *   lands in the URL then differs page by page for reasons none of them
 *   documents.
 * - The hook calls `apply` **at most once per mount**, with a patch that is by
 *   construction only keys the URL does not name. There is nothing to reset and
 *   nothing to re-derive.
 *
 * So this writes exactly what it is given and nothing else, and every page gets
 * the same behaviour by construction rather than by seven files agreeing.
 *
 * ⚠️ **`page` is deleted, always.** A saved filter set is a description of
 * *which rows*, never *which slice of them* — seeding somebody onto page 7 of a
 * result set they have not seen reads as a broken pager, and `keys` is
 * documented as never including `page` for the same reason. Deleting it here as
 * well is the belt to that braces: an incoming `?page=3` alongside no filters
 * is a stale address, and the seeded set it is about to be paired with is not
 * the set it was counted against.
 *
 * ⚠️ **`{ replace: true }`, like every other filter write in this app.** The
 * arrival write is not a navigation the reader performed; leaving it in history
 * would make Back walk through a URL rewrite they never asked for and appear to
 * do nothing.
 */
export function useSavedFilterApply<K extends string>(): (
  patch: Partial<Record<K, string>>,
) => void {
  const [searchParams, setSearchParams] = useSearchParams();
  const key = searchParams.toString();
  return useCallback(
    (patch: Partial<Record<K, string>>) => {
      const next = new URLSearchParams(key);
      for (const [name, value] of Object.entries(patch)) {
        // `''` is not a filter — every `writeState` in this app deletes an empty
        // value rather than emitting one, and `?cycleId=` is a 422 on the
        // strict list schemas rather than "no filter".
        if (typeof value !== 'string' || value === '') next.delete(name);
        else next.set(name, value);
      }
      next.delete('page');
      setSearchParams(next, { replace: true });
    },
    [key, setSearchParams],
  );
}

export interface MemorySavedFilterStore extends SavedFilterStore {
  /** What the store holds, by scope. Read it to assert on what was written. */
  readonly values: Record<string, SavedFilterValues>;
  /** How many times `save` has been called — the count a debounce test asserts. */
  writes: number;
}

/**
 * An in-memory store. Not for production — it is what lets the hook be driven
 * without a network, and what a test uses to construct the three arrival states
 * deliberately.
 */
export function createMemorySavedFilterStore(
  seed: Record<string, SavedFilterValues> = {},
): MemorySavedFilterStore {
  const values: Record<string, SavedFilterValues> = { ...seed };
  const store: MemorySavedFilterStore = {
    values,
    writes: 0,
    load(scope: string) {
      return Promise.resolve(values[scope] ?? null);
    },
    save(scope: string, next: SavedFilterValues) {
      store.writes += 1;
      values[scope] = { ...next };
      return Promise.resolve();
    },
  };
  return store;
}
